import type { Graph, GNode, GEdge, Props } from './types.ts'
import { uid, type RNG } from './util.ts'

function gcd(a: number, b: number): number {
  while (b) {
    ;[a, b] = [b, a % b]
  }
  return a
}

/**
 * A set of node ids that also supports O(1) random access and O(1) removal.
 * Backed by a dense array plus an id→index map (swap-remove on delete). This is
 * what lets the matcher pick a random seed candidate in O(1) and walk a whole
 * label class in pseudo-random order lazily — the key to O(1)-per-step
 * stochastic rewriting instead of O(bucket-size).
 */
export class LabelBucket {
  private arr: string[] = []
  private pos = new Map<string, number>()

  add(id: string) {
    if (this.pos.has(id)) return
    this.pos.set(id, this.arr.length)
    this.arr.push(id)
  }

  delete(id: string) {
    const i = this.pos.get(id)
    if (i === undefined) return
    const last = this.arr.length - 1
    const lastId = this.arr[last]
    this.arr[i] = lastId
    this.pos.set(lastId, i)
    this.arr.pop()
    this.pos.delete(id)
  }

  has(id: string): boolean {
    return this.pos.has(id)
  }

  get size(): number {
    return this.arr.length
  }

  [Symbol.iterator](): IterableIterator<string> {
    return this.arr[Symbol.iterator]()
  }

  /** Yield every id exactly once in pseudo-random order, O(1) to start. */
  *iterRandom(rng: RNG): IterableIterator<string> {
    const L = this.arr.length
    if (L === 0) return
    if (L === 1) {
      yield this.arr[0]
      return
    }
    // a stride coprime with L visits every slot exactly once (a full cycle)
    let stride = 1 + Math.floor(rng.next() * (L - 1))
    while (gcd(stride, L) !== 1) stride = (stride % (L - 1)) + 1
    const start = Math.floor(rng.next() * L)
    for (let i = 0; i < L; i++) yield this.arr[(start + i * stride) % L]
  }
}

/**
 * GraphIndex — an indexed, mutable view over a host graph that supports the
 * fast lookups the matcher and rewriter need: adjacency, label buckets, and
 * incident-edge sets. Rebuilding this is O(V+E); we keep it incrementally
 * consistent during rewriting where practical and otherwise rebuild lazily.
 */
export class GraphIndex {
  nodes = new Map<string, GNode>()
  edges = new Map<string, GEdge>()
  /** node id -> set of incident edge ids (both directions). */
  incident = new Map<string, Set<string>>()
  /** label -> bucket of node ids (random-access), for candidate pruning. */
  byLabel = new Map<string, LabelBucket>()
  /** "src|tgt" undirected key -> edge ids, to test adjacency quickly. */
  private adjPairs = new Map<string, Set<string>>()

  constructor(graph?: Graph) {
    if (graph) this.load(graph)
  }

  load(graph: Graph) {
    this.nodes.clear()
    this.edges.clear()
    this.incident.clear()
    this.byLabel.clear()
    this.adjPairs.clear()
    for (const n of graph.nodes) this.addNode(n)
    for (const e of graph.edges) this.addEdge(e)
  }

  private pairKey(a: string, b: string): string {
    return a < b ? `${a} ${b}` : `${b} ${a}`
  }

  addNode(n: GNode) {
    this.nodes.set(n.id, n)
    if (!this.incident.has(n.id)) this.incident.set(n.id, new Set())
    let bucket = this.byLabel.get(n.label)
    if (!bucket) this.byLabel.set(n.label, (bucket = new LabelBucket()))
    bucket.add(n.id)
  }

  addEdge(e: GEdge) {
    this.edges.set(e.id, e)
    this.incident.get(e.source)?.add(e.id)
    this.incident.get(e.target)?.add(e.id)
    const pk = this.pairKey(e.source, e.target)
    let s = this.adjPairs.get(pk)
    if (!s) this.adjPairs.set(pk, (s = new Set()))
    s.add(e.id)
  }

  removeNode(id: string) {
    const n = this.nodes.get(id)
    if (!n) return
    for (const eid of [...(this.incident.get(id) ?? [])]) this.removeEdge(eid)
    this.byLabel.get(n.label)?.delete(id)
    this.incident.delete(id)
    this.nodes.delete(id)
  }

  /** Change a node's label, keeping its edges, and reindex the label buckets. */
  relabelNode(id: string, label: string) {
    const n = this.nodes.get(id)
    if (!n || n.label === label) return
    this.byLabel.get(n.label)?.delete(id)
    n.label = label
    let bucket = this.byLabel.get(label)
    if (!bucket) this.byLabel.set(label, (bucket = new LabelBucket()))
    bucket.add(id)
  }

  removeEdge(id: string) {
    const e = this.edges.get(id)
    if (!e) return
    this.incident.get(e.source)?.delete(id)
    this.incident.get(e.target)?.delete(id)
    this.adjPairs.get(this.pairKey(e.source, e.target))?.delete(id)
    this.edges.delete(id)
  }

  /** All edges between two nodes (undirected). */
  edgesBetween(a: string, b: string): GEdge[] {
    const s = this.adjPairs.get(this.pairKey(a, b))
    if (!s) return []
    const out: GEdge[] = []
    for (const id of s) {
      const e = this.edges.get(id)
      if (e) out.push(e)
    }
    return out
  }

  /**
   * Allocation-free accessor: the set of edge ids between two nodes (undirected),
   * or undefined. The matcher iterates this directly in its hot path instead of
   * building a fresh array per candidate test.
   */
  edgeIdsBetween(a: string, b: string): Set<string> | undefined {
    return this.adjPairs.get(this.pairKey(a, b))
  }

  incidentEdges(id: string): GEdge[] {
    const out: GEdge[] = []
    for (const eid of this.incident.get(id) ?? []) {
      const e = this.edges.get(eid)
      if (e) out.push(e)
    }
    return out
  }

  degree(id: string): number {
    return this.incident.get(id)?.size ?? 0
  }

  toGraph(): Graph {
    return {
      nodes: [...this.nodes.values()],
      edges: [...this.edges.values()],
    }
  }
}

// --- plain helpers for building/editing graphs ---

export function emptyGraph(): Graph {
  return { nodes: [], edges: [] }
}

export function makeNode(label: string, props: Props = {}, x?: number, y?: number): GNode {
  return { id: uid('n'), label, props, x, y }
}

export function makeEdge(source: string, target: string, label = '', directed = true, props: Props = {}): GEdge {
  return { id: uid('e'), source, target, label, directed, props }
}

export function cloneGraph(g: Graph): Graph {
  return structuredClone(g)
}

/** Quick stats for the UI. */
export function graphStats(g: Graph) {
  const labels = new Map<string, number>()
  for (const n of g.nodes) labels.set(n.label, (labels.get(n.label) ?? 0) + 1)
  return {
    nodes: g.nodes.length,
    edges: g.edges.length,
    labels: [...labels.entries()].sort((a, b) => b[1] - a[1]),
  }
}
